import type { ManagedPlaylistController, PlaylistController } from '@/playback/PlaylistController'
import { BridgeDelegate, type BridgeDelegateOptions } from '@/bridge/BridgeDelegate'
import { createDisposer, type Disposable } from '@amazon/vinyl-util'

export class BridgedPlaylistController implements ManagedPlaylistController, Disposable {
    readonly cloudQueueOptions: PlaylistController['cloudQueueOptions']
    readonly queueCapabilities: PlaylistController['queueCapabilities']
    readonly current: PlaylistController['current']
    readonly next: PlaylistController['next']
    readonly playParams: PlaylistController['playParams']
    readonly skipNext: PlaylistController['skipNext']
    readonly skipPrevious: PlaylistController['skipPrevious']
    readonly invalidateNext: PlaylistController['invalidateNext']
    readonly seekToEntry: PlaylistController['seekToEntry']
    readonly clear: PlaylistController['clear']
    readonly setDataProvider: ManagedPlaylistController['setDataProvider']
    readonly queueMutatedAt: PlaylistController['queueMutatedAt']
    readonly notifyQueueMutated: PlaylistController['notifyQueueMutated']
    readonly repeatMode: PlaylistController['repeatMode']
    readonly shuffleEnabled: PlaylistController['shuffleEnabled']

    private readonly disposer = createDisposer()

    constructor(options: BridgeDelegateOptions<ManagedPlaylistController>) {
        const { add } = this.disposer
        const playlistController = options.localController
        const bridgeDelegate = add(new BridgeDelegate<PlaylistController>(options))

        this.cloudQueueOptions = bridgeDelegate.createObservableValue('cloudQueueOptions', null)
        this.queueCapabilities = bridgeDelegate.createObservableValue('queueCapabilities', null)
        this.current = bridgeDelegate.createObservableValue('current', null)
        this.next = bridgeDelegate.createObservableValue('next', null)
        this.playParams = bridgeDelegate.createObservableValue('playParams', null)

        this.skipNext = bridgeDelegate.createAsyncCommand('skipNext')
        this.skipPrevious = bridgeDelegate.createAsyncCommand('skipPrevious')
        this.invalidateNext = bridgeDelegate.createCommand('invalidateNext')
        this.seekToEntry = bridgeDelegate.createCommand('seekToEntry')
        this.clear = bridgeDelegate.createCommand('clear')
        this.queueMutatedAt = bridgeDelegate.createObservableValue('queueMutatedAt', 0)
        this.notifyQueueMutated = bridgeDelegate.createCommand('notifyQueueMutated')

        // Repeat / shuffle aren't transported over the bridge yet —
        // they're local-only flags consumed by the playerPlaylistBinding
        // which lives on the same side as the controller. Forward
        // straight to the local controller so the transport's
        // toggles still land if it ever ends up on the bridge side.
        this.repeatMode =
            playlistController?.repeatMode ??
            bridgeDelegate.createObservableValue('repeatMode', 'off' as never)
        this.shuffleEnabled =
            playlistController?.shuffleEnabled ??
            bridgeDelegate.createObservableValue('shuffleEnabled', false as never)

        // Do not transmit setting the data provider.
        this.setDataProvider =
            playlistController?.setDataProvider.bind(playlistController) ?? (() => {})
    }

    dispose() {
        this.disposer.dispose()
    }
}
